import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Bot, User, Clock, CheckCircle, AlertCircle, Loader2, ChevronDown, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { leadsAPI } from '../../services/api';
import { cn } from '../../lib/utils';

const WhatsAppChatHistory = ({ leadId, contactPhone }) => {
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSummary, setShowSummary] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await leadsAPI.getChatHistory(leadId, 200);
        setChatData(response.data);
      } catch (err) {
        console.error('Failed to fetch chat history:', err);
        setError(err.response?.data?.detail || 'Failed to load chat history');
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchChatHistory();
    }
  }, [leadId]);

  useEffect(() => {
    // Scroll to bottom when messages load
    if (messagesEndRef.current && chatData?.messages?.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatData?.messages]);

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return {};
    
    return messages.reduce((groups, message) => {
      const dateKey = message.created_at ? formatDate(message.created_at) : 'Unknown';
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
      return groups;
    }, {});
  };

  const getDeliveryStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
      case 'read':
        return <CheckCircle className="w-3 h-3 text-green-500" />;
      case 'sent':
        return <CheckCircle className="w-3 h-3 text-slate-400" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return <Clock className="w-3 h-3 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200" data-testid="chat-history-loading">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mr-2" />
          <span className="text-slate-500">Loading chat history...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-white border-slate-200" data-testid="chat-history-error">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
          <p className="text-slate-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!chatData?.has_chat_history) {
    return (
      <Card className="bg-white border-slate-200" data-testid="chat-history-empty">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            WhatsApp Chat History
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <MessageSquare className="w-16 h-16 text-slate-200 mb-4" />
          <p className="text-slate-500 text-center">
            No WhatsApp conversation history found for this lead.
          </p>
          {contactPhone && (
            <p className="text-sm text-slate-400 mt-2">
              Phone: {contactPhone}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const groupedMessages = groupMessagesByDate(chatData.messages);
  const conversation = chatData.conversation;

  return (
    <Card className="bg-white border-slate-200" data-testid="chat-history-container">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            WhatsApp Chat History
          </CardTitle>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            {chatData.total_messages} messages
          </Badge>
        </div>
        
        {/* Conversation Summary */}
        {conversation && conversation.summary && (
          <Collapsible open={showSummary} onOpenChange={setShowSummary} className="mt-3">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between px-3 py-2 h-auto bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-700">AI Conversation Summary</span>
                </div>
                <ChevronDown className={cn("w-4 h-4 text-purple-600 transition-transform", showSummary && "rotate-180")} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 p-2 sm:p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{conversation.summary}</p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-3 pt-2 border-t border-purple-100">
                  <span className="text-xs text-slate-500">
                    Total Tokens: <span className="font-medium text-purple-700">{conversation.total_tokens_used?.toLocaleString() || 0}</span>
                  </span>
                  {conversation.first_message_at && (
                    <span className="text-xs text-slate-500">
                      Started: {formatDateTime(conversation.first_message_at)}
                    </span>
                  )}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Chat Messages */}
        <ScrollArea className="h-[350px] sm:h-[400px] px-2 sm:px-4">
          <div className="py-4 space-y-4">
            {Object.entries(groupedMessages).map(([dateKey, messages]) => (
              <div key={dateKey}>
                {/* Date Separator */}
                <div className="flex items-center justify-center my-4">
                  <div className="bg-slate-100 text-slate-500 text-xs px-3 py-1 rounded-full">
                    {dateKey}
                  </div>
                </div>
                
                {/* Messages for this date */}
                <div className="space-y-3">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      data-testid={`chat-message-${message.id}`}
                      className={cn(
                        "flex",
                        message.is_outbound ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] sm:max-w-[80%] rounded-lg p-2 sm:p-3 shadow-sm",
                          message.is_outbound
                            ? "bg-green-100 border border-green-200"
                            : "bg-white border border-slate-200"
                        )}
                      >
                        {/* Message Header */}
                        <div className="flex items-center gap-2 mb-1">
                          {message.is_outbound ? (
                            <Bot className="w-4 h-4 text-green-700" />
                          ) : (
                            <User className="w-4 h-4 text-slate-600" />
                          )}
                          <span className={cn(
                            "text-xs font-medium",
                            message.is_outbound ? "text-green-700" : "text-slate-600"
                          )}>
                            {message.is_outbound ? 'AI Response' : 'Customer'}
                          </span>
                          <span className="text-xs text-slate-400 ml-auto">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                        
                        {/* Message Content */}
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">
                          {message.content || '(No content)'}
                        </p>
                        
                        {/* AI Analysis Info (for inbound messages) */}
                        {!message.is_outbound && (message.ai_summary || message.intent) && (
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            {message.intent && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 mr-2">
                                {message.intent}
                              </Badge>
                            )}
                            {message.is_lead && (
                              <Badge className="text-xs bg-amber-100 text-amber-700 mr-2">
                                Lead ({Math.round((message.confidence || 0) * 100)}%)
                              </Badge>
                            )}
                            {message.tokens_used && (
                              <span className="text-xs text-slate-400">
                                Tokens: {message.tokens_used}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Delivery Status (for outbound messages) */}
                        {message.is_outbound && (
                          <div className="flex items-center justify-end gap-1 mt-1">
                            {getDeliveryStatusIcon(message.delivery_status)}
                            <span className="text-xs text-slate-400 capitalize">
                              {message.delivery_status || 'pending'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default WhatsAppChatHistory;
